/**
 * primitives.js — buttons, badges, cards, alerts, spinners.
 *
 * Replaces react-bootstrap. v1 used Button in 59 files, Form in 47, Modal in
 * 28 and then fought their styles with 420 !important declarations. These
 * components are ~200 lines total, carry no runtime dependency, and are styled
 * entirely from design tokens.
 */

import { forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { ErrorIcon, InfoIcon, SuccessIcon, WarningIcon } from './icons';
import { usePublishPageHeading } from '../layout/page-heading';

/* ------------------------------------------------------------------ *
 * Button
 * ------------------------------------------------------------------ */

const cx = (...parts) => parts.filter(Boolean).join(' ');

/* Icons are sized against the label, not against the button box, so a 13px
   label never sits beside a 16px glyph. */
const BUTTON_ICON_SIZE = { sm: 14, md: 15, lg: 16 };

export const Button = forwardRef(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    icon: Icon = null,
    iconOnly = false,
    block = false,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx(
        'btn',
        `btn-${variant}`,
        size !== 'md' && `btn-${size}`,
        iconOnly && 'btn-icon-only',
        block && 'btn-block',
        className
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span className="btn-spinner" aria-hidden="true" />
      ) : (
        Icon && <Icon size={BUTTON_ICON_SIZE[size] || BUTTON_ICON_SIZE.md} aria-hidden="true" />
      )}
      {!iconOnly && children}
    </button>
  );
});

/* ------------------------------------------------------------------ *
 * Badge
 * ------------------------------------------------------------------ */

export function Badge({ tone = 'neutral', dot = false, children, className, ...rest }) {
  return (
    <span className={cx('badge', `badge-${tone}`, className)} {...rest}>
      {dot && <span className="badge-dot" aria-hidden="true" />}
      {children}
    </span>
  );
}

/** Maps a voucher status to a badge. Centralised so the label is consistent. */
export function StatusBadge({ status, balanceDue, dueDate }) {
  if (status === 'cancelled') return <Badge tone="neutral" dot>Cancelled</Badge>;
  if (status === 'settled' || balanceDue === 0) return <Badge tone="success" dot>Paid</Badge>;

  if (dueDate) {
    const due = dueDate?.toDate ? dueDate.toDate() : new Date(dueDate);
    if (!Number.isNaN(due.getTime()) && due < new Date()) {
      return <Badge tone="danger" dot>Overdue</Badge>;
    }
  }
  return <Badge tone="warning" dot>Unpaid</Badge>;
}

/**
 * The lifecycle badge for a NON-POSTING document.
 *
 * A quotation is not "unpaid" — nobody owes anything on it — and a sales order
 * is not "overdue" when its delivery date passes without an invoice. Feeding
 * those documents through `StatusBadge` produced exactly that: every open
 * quotation in the list marked Unpaid in warning amber, which is both wrong
 * and, worse, indistinguishable from a real receivable.
 *
 * So a document that does not touch the ledger gets a badge about the only
 * thing it does have — whether it has been acted on.
 */
export function FulfilmentBadge({ status, label, tone }) {
  if (!status) return null;
  return (
    <Badge tone={tone || 'neutral'} dot>
      {label || status}
    </Badge>
  );
}

/* ------------------------------------------------------------------ *
 * Card
 * ------------------------------------------------------------------ */

export function Card({ className, children, ...rest }) {
  return (
    <section className={cx('card', className)} {...rest}>
      {children}
    </section>
  );
}

/** The glyph in a card header is a fixed 16px. It sits in a 32px tile and the
 *  tile is the thing you see, so letting each screen pick a size only made the
 *  tiles disagree with one another. */
const CARD_ICON_SIZE = 16;

/**
 * `icon` IS A COMPONENT, not an element — `icon={ItemIcon}`, never
 * `icon={<ItemIcon />}`. Same contract as Button and EmptyState, so there is
 * one rule to remember rather than three.
 *
 * It used to be that a card wanting a glyph had to build one by hand:
 *
 *     title={<span className="row gap-2"><ItemIcon size={ICON_SIZE.md} …/> Items</span>}
 *
 * Fifty-two screens did exactly that, each choosing its own icon size and its
 * own gap, and the header lost its <h2> in the process — the title stopped
 * being a heading and became a span inside one. Passing the icon in restores
 * the heading and makes every card's marker the same shape and size.
 */
export function CardHeader({ title, description, actions, icon: Icon, children }) {
  if (children) return <header className="card-header">{children}</header>;
  return (
    <header className="card-header">
      <div className="row gap-3">
        {Icon && (
          <span className="card-icon" aria-hidden="true">
            <Icon size={CARD_ICON_SIZE} />
          </span>
        )}
        <div className="stack">
          {title && <h2 className="card-title">{title}</h2>}
          {description && <p className="card-description">{description}</p>}
        </div>
      </div>
      {actions && <div className="row gap-2">{actions}</div>}
    </header>
  );
}

export function CardBody({ flush = false, className, children }) {
  return <div className={cx('card-body', flush && 'card-body-flush', className)}>{children}</div>;
}

export function CardFooter({ children }) {
  return <footer className="card-footer">{children}</footer>;
}

/* ------------------------------------------------------------------ *
 * Alert
 * ------------------------------------------------------------------ */

const ALERT_ICONS = {
  info: InfoIcon,
  success: SuccessIcon,
  warning: WarningIcon,
  danger: ErrorIcon,
};

export function Alert({ tone = 'info', title, children, actions, className }) {
  const Icon = ALERT_ICONS[tone] || InfoIcon;
  return (
    <div
      className={cx('alert', `alert-${tone}`, className)}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      <Icon className="alert-icon" size={15} aria-hidden="true" />
      <div className="alert-body">
        {title && <div className="alert-title">{title}</div>}
        {children && <div>{children}</div>}
        {actions && <div className="row gap-2 mt-2">{actions}</div>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Spinner / loading
 * ------------------------------------------------------------------ */

export function Spinner({ size = 'md', label = 'Loading' }) {
  return (
    <span
      className={cx('spinner', size === 'lg' && 'spinner-lg')}
      role="status"
      aria-label={label}
    />
  );
}

export function LoadingBlock({ label = 'Loading…' }) {
  return (
    <div className="loading-block">
      <Spinner />
      <span>{label}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Page header
 * ------------------------------------------------------------------ */

/**
 * The screen's heading and its actions.
 *
 * Inside the app shell the title and subtitle are HOISTED into the topbar, on
 * the same line as the offline, theme and account controls, and what stays
 * here is a right-aligned toolbar of actions. Outside the shell — sign-in,
 * onboarding — there is no topbar to hoist into, so the full header renders in
 * place exactly as before. Screens do not have to know the difference.
 */
export function PageHeader({ title, subtitle, breadcrumbs, actions }) {
  const { hoisted, actionsSlot } = usePublishPageHeading({ title, subtitle });

  if (hoisted) {
    return (
      <>
        {/* The buttons are PORTALLED into the topbar rather than passed to it,
            because a screen builds a new element every render and putting that
            in shell state would loop forever. See layout/page-heading.js. */}
        {actions && actionsSlot && createPortal(actions, actionsSlot)}
        {/* Until the slot exists — one render, on first mount — the actions
            stay in the content area rather than vanishing. */}
        {actions && !actionsSlot && <div className="page-toolbar">{actions}</div>}
        {breadcrumbs && (
          <nav className="breadcrumbs page-toolbar-breadcrumbs" aria-label="Breadcrumb">
            {breadcrumbs}
          </nav>
        )}
      </>
    );
  }

  return (
    <div className="page-header">
      <div className="page-header-titles">
        {breadcrumbs && <nav className="breadcrumbs" aria-label="Breadcrumb">{breadcrumbs}</nav>}
        <h1 className="page-title">{title}</h1>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="page-header-actions">{actions}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Tabs
 * ------------------------------------------------------------------ */

export function Tabs({ tabs, value, onChange, ariaLabel = 'Sections' }) {
  return (
    <div className="tabs" role="tablist" aria-label={ariaLabel}>
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          className="tab"
          aria-selected={value === tab.value}
          onClick={() => onChange(tab.value)}
        >
          {tab.label}
          {tab.count !== undefined && <span className="tab-count">{tab.count}</span>}
        </button>
      ))}
    </div>
  );
}

export { cx };
